import { useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Typography,
  Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Stack, useMediaQuery } from '@mui/system';
import { theme } from '@pagopa/mui-italia';

import { useTranslation } from 'react-i18next';
import { Link as LinkRouter } from 'react-router';

import Building from '../../../icons/Building';
import Euro from '../../../icons/Euro';
import Leggi from '../../../icons/Leggi';
import SEO from '../../SEO/Seo';
import { links } from '../../../links';
import { Card } from './card';

export default function Home() {
  const { t } = useTranslation();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const mainTitleRef = useRef<HTMLHeadingElement>(null);

  // smooth scroll without adding anchor to url
  const handleDiscoverClick = (e: React.MouseEvent) => {
    e.preventDefault();
    mainTitleRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO title={t('home.heroTitle')} description={t('home.heroDesc')} />
      <Box
        bgcolor={'background.paper'}
        color={'primary.contrastText'}
        px={{ xs: 2, md: 0 }}
      >
        <Container maxWidth={'md'}>
          <Breadcrumbs
            aria-label="breadcrumbs"
            sx={{ pt: 4, pb: 5 }}
            separator="›"
          >
            <Link
              component={LinkRouter}
              display="flex"
              alignItems="center"
              color={smUp ? 'textSecondary' : 'textPrimary'}
              fontWeight={smUp ? '400' : '600'}
              to={links.public}
              underline="none"
            >
              {!smUp && <ArrowBackIcon style={{ marginRight: 8 }} />}
              Home
            </Link>
            {smUp && (
              <Typography
                color="textPrimary"
                fontWeight={'600'}
                aria-current="page"
              >
                {t('home.title')}
              </Typography>
            )}
          </Breadcrumbs>
          <Stack gap={10}>
            <Stack gap={3}>
              <Stack gap={2}>
                <Typography
                  variant="h2"
                  component={'h1'}
                  lineHeight={{ md: '3.2rem' }}
                >
                  {t('home.heroTitle')}
                </Typography>
                <Typography color="textSecondary" component="p">
                  {t('home.heroDesc')}
                </Typography>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={3}>
                <Box>
                  <Button variant="contained" href={links.payNow}>
                    {t('commons.payNow')}
                  </Button>
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    aria-label={t('commons.discoverHow')}
                    aria-controls={t('home.mainTitle')}
                    onClick={handleDiscoverClick}
                  >
                    {t('commons.discoverHow')}
                  </Button>
                </Box>
              </Stack>
            </Stack>
            <Stack gap={5} component={'section'}>
              <Stack spacing={2}>
                <Typography variant={'h4'} component={'h2'} ref={mainTitleRef}>
                  {t('home.mainTitle')}
                </Typography>
                <Typography
                  variant={'body1'}
                  component={'p'}
                  color={'textSecondary'}
                >
                  {t('home.mainSubtitle')}
                </Typography>
              </Stack>
              <Stack
                component={'ul'}
                gap={3}
                p={0}
                m={0}
                direction={{ xs: 'column', md: 'row' }}
                id="mainBoxes"
              >
                <Card
                  icon={<Building title="Icona comune" />}
                  title={t('home.mainBoxes.0.title')}
                  description={t('home.mainBoxes.0.desc')}
                />
                <Card
                  icon={<Euro title="Icona euro" />}
                  title={t('home.mainBoxes.1.title')}
                  description={t('home.mainBoxes.1.desc')}
                  action={{
                    label: t('commons.payNow'),
                    href: links.payNow
                  }}
                />
                <Card
                  icon={<Leggi title="Icona ricevuta" />}
                  title={t('home.mainBoxes.2.title')}
                  description={t('home.mainBoxes.2.desc')}
                  action={{
                    label: t('home.mainBoxes.2.button'),
                    href: links.guide
                  }}
                />
              </Stack>
            </Stack>
            <Stack
              id="faqBox"
              alignItems={{ xs: 'center', md: 'flex-start' }}
              mb={{ xs: 10, md: 12 }}
            >
              <Typography variant={'h4'} component={'h2'}>
                {t('home.faqBox.title')}
              </Typography>
              <Box>
                <Button
                  component={LinkRouter}
                  to="/faq"
                  variant="contained"
                  color="primary"
                  sx={{ mt: 4 }}
                >
                  {t('home.faqBox.cta')}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
